import { defineComponent as _defineComponent } from 'vue'
import { ComputedRef, computed, provide, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'

import TripShareSlideOver from '../shareTrip/TripShareSlideOver.vue'
import DropdownMenu from '@/components/core/DropdownMenu.vue'
import DropdownMenuItem from '@/components/core/DropdownMenuItem.vue'
import TripSidebarSection from '@/components/trip/sidebar/TripSidebarSection.vue'
import StackedAttendeesGroup from '@/components/home/StackedAttendeesGroup.vue'
import TripSettings from '@/components/trip/settings/TripSettings.vue'
import TripSidebarSectionItem, {
  type SidebarSectionItemProps,
} from '@/components/trip/sidebar/TripSidebarSectionItem.vue'
import { isExpandedSymbol } from '@/components/trip/sidebar/utils'
import { useContext } from '@/composables/context'
import { useCreateConversation } from '@/composables/assistant'
import { useSelectedTrip } from '@/composables/trip'
import { eventModule } from '@/store'
import { logEvent } from '@/utils/utility-manager'

export interface TripSidebarContentProps {
  expandAllTime?: boolean
  isExpanded: boolean
}

type TripSidebarItemType = SidebarSectionItemProps & { onClick: () => void }


export default /*#__PURE__*/_defineComponent({
  __name: 'TripSidebarContent',
  props: {
    expandAllTime: { type: Boolean },
    isExpanded: { type: Boolean }
  },
  emits: ['update:isExpanded'],
  setup(__props: any, { emit: emits }) {

const props = __props as {
  expandAllTime?: boolean
  isExpanded: boolean
};




const router = useRouter()
const route = useRoute()
const { i18n, $config } = useContext()

const trip = useSelectedTrip()

const { mutateAsync: createConversation } = useCreateConversation()

const expandTripSidebar = computed({
  get: () => props.isExpanded,
  set: (value) => {
    if (props.expandAllTime) {
      return
    }
    emits('update:isExpanded', value)
  },
})
provide(isExpandedSymbol, expandTripSidebar)

const stackedAttendeesGroupRef = ref<InstanceType<
  typeof StackedAttendeesGroup
> | null>(null)

function navigateToTripElement(path: string, isProFeature = false) {
  if (isProFeature && !trip.value?.activeProSubscription) {
    eventModule.toggleProHintModal()
    return
  }

  eventModule.toggleNavigationDrawer(false)
  router.push(path)
}

const planningItems: ComputedRef<TripSidebarItemType[]> = computed(() => {
  const items: TripSidebarItemType[] = [
    {
      text: i18n.t('overview').toString(),
      image: require('assets/images/widget_overview.svg'),
      color: 'primary',
      isActive: route.matched.some(({ path }) => path === '/trip/:id'),
      onClick: () => navigateToTripElement(`/trip/${trip.value?.id}`),
    },
    {
      text: i18n.t('travelPlan').toString(),
      image: require('assets/images/widget_travelplan.svg'),
      color: 'travelplanGreen',
      isActive: route.matched.some(({ path }) => path.includes('/waypoints')),
      onClick: () => navigateToTripElement(`/trip/${trip.value?.id}/waypoints`),
    },
    {
      text: i18n.t('expenses').toString(),
      image: require('assets/images/widget_expenses.svg'),
      color: 'expenseYellow',
      isActive: route.matched.some(({ path }) => path.includes('/expenses')),
      onClick: () => navigateToTripElement(`/trip/${trip.value?.id}/expenses`),
    },
    {
      text: i18n.t('notes').toString(),
      image: require('assets/images/widget_notes.svg'),
      color: 'listYellow',
      isActive: route.matched.some(({ path }) => path.includes('/notes')),
      onClick: () => navigateToTripElement(`/trip/${trip.value?.id}/notes`),
    },
    {
      text: i18n.t('lists').toString(),
      image: require('assets/images/widget_packinglists.svg'),
      color: 'listYellow',

      isActive: route.matched.some(({ path }) =>
        path.includes('/packing-lists'),
      ),
      onClick: () =>
        navigateToTripElement(`/trip/${trip.value?.id}/packing-lists`, true),
    },
    {
      text: i18n.t('documents').toString(),
      image: require('assets/images/widget_documents.svg'),
      color: 'documentBlue',
      isActive: route.matched.some(({ path }) => path.includes('/documents')),
      onClick: () => navigateToTripElement(`/trip/${trip.value?.id}/documents`),
    },
    {
      text: i18n.t('photos').toString(),
      image: require('assets/images/widget_photos.svg'),
      color: 'photoRed',
      isActive: route.matched.some(({ path }) => path.includes('/photos')),
      onClick: () => navigateToTripElement(`/trip/${trip.value?.id}/photos`),
    },
  ]

  if ($config.enableTripAssistant) {
    items.splice(2, 0, {
      text: 'Assistant',
      icon: '$shimmer',
      color: 'assistantPurple',
      isActive: route.matched.some(({ path }) =>
        path.includes('/conversation'),
      ),
      onClick: async () => {
        const { conversation } = await createConversation({
          tripId: trip.value.id,
        })
        navigateToTripElement(
          `/trip/${trip.value?.id}/conversation/${conversation.shortId}`,
        )
      },
    })
  }

  return items
})

const showSettingsDialog = ref(false)

function openSettingsDialog() {
  eventModule.toggleNavigationDrawer(false)
  showSettingsDialog.value = true
}

const showTripSharingOptions = ref(false)

function openShareTripOptions() {
  eventModule.toggleNavigationDrawer(false)

  logEvent('share_trip_click', { source: 'sidebar' })
  showTripSharingOptions.value = true
}

return { __sfc: true,props, emits, router, route, i18n, $config, trip, createConversation, expandTripSidebar, stackedAttendeesGroupRef, navigateToTripElement, planningItems, showSettingsDialog, openSettingsDialog, showTripSharingOptions, openShareTripOptions, TripShareSlideOver, DropdownMenu, DropdownMenuItem, TripSidebarSection, StackedAttendeesGroup, TripSettings, TripSidebarSectionItem }
}

})